import type { Action, Reducer } from 'redux';
import { IApplicationState, IAppThunkAction } from '.';
import { IPaymentShare, ISplitPaymentShareRequest } from '../interfaces/ISplitPayment';
import SplitPaymentService from '../service/splitPayments';
import { appInsights } from '../Logging/appInsights';
import {
    CLEAR_BOOKING,
    EXPIRE_BOOKING_SUCCESS,
    EXPIRE_BOOKING_COMPLETE,
    IClearBookingAction,
    IExpireBookingSuccessAction,
    IExpireBookingCompletedAction,
} from './Booking';
import { CLEAR_GIFT_VOUCHER, IClearGiftVoucherAction } from './GiftVoucher';
import { SITE_SELECTED, ISiteSelectedAction } from './Sites';
import { actionCreators as UIActions } from './UI';

export type PaymentOptionType = 'full' | 'deposit' | 'split';

export interface IPaymentOptionState {
    selectedOption: PaymentOptionType;
    splitSlotCount: number;
    splitShares: IPaymentShare[];
    splitCreated: boolean;
    splitLoading: boolean;
}

export const SET_PAYMENT_OPTION: 'SET_PAYMENT_OPTION' = 'SET_PAYMENT_OPTION';
export const SET_SPLIT_SLOT_COUNT: 'SET_SPLIT_SLOT_COUNT' = 'SET_SPLIT_SLOT_COUNT';
export const CREATE_SPLIT_REQUESTED: 'CREATE_SPLIT_REQUESTED' = 'CREATE_SPLIT_REQUESTED';
export const CREATE_SPLIT_SUCCESS: 'CREATE_SPLIT_SUCCESS' = 'CREATE_SPLIT_SUCCESS';
export const CREATE_SPLIT_FAILED: 'CREATE_SPLIT_FAILED' = 'CREATE_SPLIT_FAILED';
export const RESET_PAYMENT_OPTION: 'RESET_PAYMENT_OPTION' = 'RESET_PAYMENT_OPTION';

export interface ISetPaymentOptionAction extends Action {
    type: typeof SET_PAYMENT_OPTION;
    option: PaymentOptionType;
}

export interface ISetSplitSlotCountAction extends Action {
    type: typeof SET_SPLIT_SLOT_COUNT;
    slotCount: number;
}

export interface ICreateSplitRequestedAction extends Action {
    type: typeof CREATE_SPLIT_REQUESTED;
}

export interface ICreateSplitSuccessAction extends Action {
    type: typeof CREATE_SPLIT_SUCCESS;
    shares: IPaymentShare[];
}

export interface ICreateSplitFailedAction extends Action {
    type: typeof CREATE_SPLIT_FAILED;
}

export interface IResetPaymentOptionAction extends Action {
    type: typeof RESET_PAYMENT_OPTION;
}

export type KnownPaymentOptionActions =
    | ISetPaymentOptionAction
    | ISetSplitSlotCountAction
    | ICreateSplitRequestedAction
    | ICreateSplitSuccessAction
    | ICreateSplitFailedAction
    | IResetPaymentOptionAction
    | IClearBookingAction
    | IExpireBookingSuccessAction
    | IExpireBookingCompletedAction
    | IClearGiftVoucherAction
    | ISiteSelectedAction;

export const actionCreators = {
    setPaymentOption:
        (option: PaymentOptionType): IAppThunkAction<KnownPaymentOptionActions> =>
        (dispatch) => {
            dispatch({ type: SET_PAYMENT_OPTION, option });
        },

    setSplitSlotCount:
        (slotCount: number): IAppThunkAction<KnownPaymentOptionActions> =>
        (dispatch) => {
            dispatch({ type: SET_SPLIT_SLOT_COUNT, slotCount });
        },

    createSplit:
        (shares: ISplitPaymentShareRequest[], deadlineHours?: number): IAppThunkAction<KnownPaymentOptionActions> =>
        (dispatch: any, getState) => {
            const appState: IApplicationState = getState();
            const booking = appState.booking.booking;
            const splitSlotCount = appState.paymentOption.splitSlotCount;

            if (!booking?.invoiceId || !booking?.siteId) return;

            dispatch({ type: CREATE_SPLIT_REQUESTED });

            SplitPaymentService.CreateSplit(booking.siteId, booking.invoiceId, {
                shares,
                deadlineHours,
                organizerSlotCount: splitSlotCount,
            })
                .then((data) => {
                    dispatch({ type: CREATE_SPLIT_SUCCESS, shares: data });
                })
                .catch((error: Error) => {
                    dispatch({ type: CREATE_SPLIT_FAILED });
                    appInsights?.trackException(
                        { error: new Error('Error creating split payment'), severityLevel: 2 },
                        { exception: error, sourceFile: 'PaymentOption.ts', product: 'booking' }
                    );
                    dispatch(UIActions.displayError('errors.splitPayment.createFailed'));
                });
        },

    resetPaymentOption: (): IAppThunkAction<KnownPaymentOptionActions> => (dispatch) => {
        dispatch({ type: RESET_PAYMENT_OPTION });
    }
};

const unloadedState: IPaymentOptionState = {
    selectedOption: 'full',
    splitSlotCount: 1,
    splitShares: [],
    splitCreated: false,
    splitLoading: false,
};

export const reducer: Reducer<IPaymentOptionState> = (
    state: IPaymentOptionState | undefined,
    incomingAction: Action
): IPaymentOptionState => {
    if (state === undefined) return unloadedState;

    const action = incomingAction as KnownPaymentOptionActions;
    switch (action.type) {
        case SET_PAYMENT_OPTION:
            return {
                ...state,
                selectedOption: action.option,
            };
        case SET_SPLIT_SLOT_COUNT:
            return {
                ...state,
                splitSlotCount: action.slotCount,
            };
        case CREATE_SPLIT_REQUESTED:
            return {
                ...state,
                splitLoading: true,
            };
        case CREATE_SPLIT_SUCCESS:
            return {
                ...state,
                splitLoading: false,
                splitCreated: true,
                splitShares: action.shares,
            };
        case CREATE_SPLIT_FAILED:
            return {
                ...state,
                splitLoading: false,
            };
        case CLEAR_BOOKING:
        case CLEAR_GIFT_VOUCHER:
        case EXPIRE_BOOKING_SUCCESS:
        case EXPIRE_BOOKING_COMPLETE:
        case SITE_SELECTED:
        case RESET_PAYMENT_OPTION:
            return unloadedState;
        default:
            return state;
    }
};
